"use client";
import { useRouter } from "next/navigation";
import { useState, useEffect } from 'react';
import { AccountSettings } from '@/components/dashboard/AccountSettings';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Sparkles, ExternalLink, CreditCard, ArrowRight, User } from 'lucide-react';
import { PageHeader } from "@/components/PageHeader";
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';


export default function AeoAccount() {
  const { user } = useAuth();
  const router = useRouter();
  const [planName, setPlanName] = useState<string | null>(null);
  const [isTrialing, setIsTrialing] = useState(false);

  useEffect(() => {
    const loadPlan = async () => {
      if (!user) return;

      const { data: profile } = await supabase
        .from('profiles')
        .select('full_name')
        .eq('id', user.id)
        .single();

      if (profile) {
        setPlanName('Free');
      }
    };

    loadPlan();
  }, [user]);

  return (
    <div className="space-y-6 max-w-2xl mx-auto">
      <PageHeader
        icon={User}
        title="My Account"
        description="Manage your profile and subscription"
        gradientFrom="from-indigo-500/10"
        gradientVia="via-blue-500/10"
        gradientTo="to-sky-500/10"
        iconFrom="from-indigo-500"
        iconTo="to-blue-600"
      />
      {planName && (
          <Badge variant="secondary" className="bg-primary/10 text-primary">
            <Sparkles className="w-3 h-3 mr-1" />
            {isTrialing ? 'Free trial' : planName}
          </Badge>
      )}

      {/* Profile Settings */}
      <Card className="p-6">
        <h2 className="text-lg font-semibold mb-4">Profile Settings</h2>
        <AccountSettings />
      </Card>

      {/* Quick Actions */}
      <div className="grid gap-4 md:grid-cols-2">
        {/* Subscription Card */}
        <Card className="p-6 hover:shadow-md transition-shadow cursor-pointer" onClick={() => router.push('/subscription')}>
          <div className="flex items-start gap-4">
            <div className="p-2 rounded-lg bg-primary/10">
              <CreditCard className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold">Manage subscription</h3>
              <p className="text-sm text-muted-foreground">View plans and upgrade</p>
            </div>
            <ArrowRight className="h-5 w-5 text-muted-foreground" />
          </div>
        </Card>

        {/* External Link Card */}
        <Card className="p-6 hover:shadow-md transition-shadow cursor-pointer">
          <div className="flex items-start gap-4">
            <div className="p-2 rounded-lg bg-muted">
              <ExternalLink className="h-5 w-5 text-muted-foreground" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold">Documentation</h3>
              <p className="text-sm text-muted-foreground">Guides and resources</p>
            </div>
            <ArrowRight className="h-5 w-5 text-muted-foreground" />
          </div>
        </Card>
      </div>

      {/* Link to autopilotgeo.com */}
      <div className="text-center pt-4">
        <a 
          href="https://autopilotgeo.com" 
          target="_blank" 
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-pink-600 transition-colors text-sm"
        >
          <ExternalLink className="h-4 w-4" />
          autopilotgeo.com
        </a>
      </div>
    </div>
  );
}